import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {InputAdornment} from '@mui/material';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type {SxProps} from '@mui/system';
import {lighten} from '@mui/system/colorManipulator';
import {type FC, type KeyboardEvent, useEffect, useState} from 'react';
import {primaryColor, secondaryColor} from '@/theme/theme';
import {hasDecimalSeparator, isDecimalSeparatorKey, parseDecimalInput, sanitizeDecimalInput} from './decimalInput';

// TYPES
type Props = {
  editable: boolean;
  value: number;
  onSave: (value: number) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  unit?: string;
};

const InputNumberEditableOnHover: FC<Props> = ({editable, value, onSave, placeholder = '-', min, max, unit}) => {
  const [editing, setEditing] = useState(false);
  const [hover, setHover] = useState(false);
  const [rawValue, setRawValue] = useState<string>(String(value));

  useEffect(() => {
    setRawValue(String(value));
  }, [value]);

  const handleMouseEnter = () => editable && setHover(true);
  const handleMouseLeave = () => editable && setHover(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRawValue(sanitizeDecimalInput(e.target.value));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ];
    if (
      !/^[0-9]$/.test(e.key) &&
      !allowedKeys.includes(e.key) &&
      !(isDecimalSeparatorKey(e.key) && !hasDecimalSeparator(rawValue))
    ) {
      e.preventDefault();
    }
  };

  const commitValue = () => {
    const parsed = parseDecimalInput(rawValue);
    if (parsed === undefined) {
      setRawValue(String(value));
      return;
    }
    if (min !== undefined && parsed < min) {
      setRawValue(String(value));
      return;
    }
    if (max !== undefined && parsed > max) {
      setRawValue(String(value));
      return;
    }
    if (parsed !== value) {
      onSave(parsed);
    } else {
      setRawValue(String(value));
    }
  };

  const handleBlur = () => {
    setEditing(false);
    commitValue();
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={styles(hover, editing).container}
    >
      <TextField
        disabled={!editable}
        value={rawValue}
        placeholder={placeholder}
        type="text"
        inputMode="decimal"
        variant="standard"
        onClick={() => setEditing(true)}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={(e) => e.target.select()}
        onBlur={handleBlur}
        size="small"
        slotProps={{
          input: {
            endAdornment: unit ? (
              <InputAdornment position="end">
                <Typography sx={{mb: 0.5}}>{unit}</Typography>
              </InputAdornment>
            ) : undefined,
          },
        }}
        sx={styles(hover, editing).input}
      />
      <EditOutlinedIcon color="primary" sx={styles(hover, editing).icon} />
    </Stack>
  );
};
export default InputNumberEditableOnHover;

const styles = (hover: boolean, editing: boolean): Record<string, SxProps> => ({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
    border: editing || hover ? '1px solid #d3d3d3' : '1px solid transparent',
    backgroundColor: editing || hover ? lighten(secondaryColor, 0.9) : 'white',
    transition: 'all 500ms ease-in-out',
    pr: 0.5,
    width: '100%',
  },
  input: {
    pt: 0.5,
    pl: '4px',
    width: '100%',
    height: '34px',
    '& .MuiInput-root': {
      fontWeight: 900,
      '&::before': {
        borderBottom: editing ? `1px solid ${primaryColor}` : '1px solid transparent',
      },
      '&::after': {
        borderBottom: editing ? '2px solid' : '1px solid transparent',
      },
      '&:hover:not(.Mui-disabled)::before': {
        borderBottom: editing ? `1px solid ${primaryColor}` : '1px solid transparent',
      },
    },
  },
  icon: {
    opacity: hover ? 1 : 0,
    transition: 'opacity 500ms ease-in-out',
  },
});
